import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import "./About.css";

const calculateYearsExperience = (workExp) => {
  if (!workExp || workExp.length === 0) return 0;
  const dates = workExp
    .map((job) => new Date(job.from.replace(/(\d+)(st|nd|rd|th)/, "$1")))
    .filter((d) => !isNaN(d));
  if (dates.length === 0) return 0;
  const earliest = new Date(Math.min(...dates));
  const today = new Date();
  return Math.floor((today - earliest) / (1000 * 60 * 60 * 24 * 365.25));
};

const About = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });
  const yearsExperience = calculateYearsExperience(data.workExp);
  const projectsCompleted = data.projects
    ? data.projects.filter((p) => p !== null).length
    : 0;
  const companiesWorked = data.workExp ? data.workExp.length : 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const skillCategories = {
    Frontend: ["React.js", "Next.js", "TypeScript", "JavaScript", "HTML/CSS"],
    Backend: ["Node.js", "Python", "Express.js", "REST APIs"],
    Database: ["MongoDB", "PostgreSQL", "MySQL"],
    "Cloud & Tools": ["AWS", "Git", "Docker", "Salesforce"],
    "AI/ML": [
      "Machine Learning",
      "TensorFlow",
      "Scikit-learn",
      "Data Analysis",
    ],
  };

  return (
    <section className="about" id="about" ref={ref}>
      <div className="container">
        <motion.div
          className="about-content"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div className="section-header" variants={itemVariants}>
            <h2 className="section-title">About Me</h2>
            <div className="section-line"></div>
          </motion.div>

          <div className="about-grid">
            <motion.div className="about-text" variants={itemVariants}>
              <div className="about-description">
                {data.about.split("  ").map((paragraph, index) => (
                  <p key={index}>{paragraph.trim()}</p>
                ))}
              </div>
            </motion.div>

            <motion.div className="about-skills" variants={itemVariants}>
              <h3>Technologies & Expertise</h3>
              <div className="skills-categories">
                {Object.entries(skillCategories).map(
                  ([category, skills], categoryIndex) => (
                    <motion.div
                      key={category}
                      className="skill-category"
                      initial={{ opacity: 0, y: 20 }}
                      animate={
                        isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                      }
                      transition={{ delay: categoryIndex * 0.1, duration: 0.5 }}
                    >
                      <h4 className="category-title">{category}</h4>
                      <div className="skills-grid">
                        {skills.map((skill, index) => (
                          <motion.div
                            key={skill}
                            className="skill-item"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={
                              isInView
                                ? { opacity: 1, scale: 1 }
                                : { opacity: 0, scale: 0.8 }
                            }
                            transition={{
                              delay: categoryIndex * 0.1 + index * 0.05,
                              duration: 0.3,
                            }}
                          >
                            {skill}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          </div>

          <motion.div className="about-stats" variants={itemVariants}>
            <div className="stat-item">
              <div className="stat-number">{yearsExperience}+</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{projectsCompleted}+</div>
              <div className="stat-label">Projects Completed</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{companiesWorked}</div>
              <div className="stat-label">Companies Worked</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
